import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { CollapsibleArrow } from '@avila-tek/ui/src';
import { motion } from 'framer-motion';
import { useUser } from '../../../hooks';
import { roles } from '../../../static';
import { IRoute } from '../../../routes';

const itemVariant = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
  },
};

interface SidebarItemProps {
  href?: string;
  as?: string;
  icon?: React.ReactNode;
  text: string;
  isCollapsible?: boolean;
  subLinks?: Array<Partial<IRoute>>;
}

export default function SidebarItem({
  href,
  as,
  icon,
  text,
  isCollapsible = false,
  subLinks = [],
}: SidebarItemProps) {
  const router = useRouter();
  const [user] = useUser();
  const [isOpen, setOpen] = React.useState(false);
  const toggle = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setOpen(!isOpen);
  };

  const textColor =
    user?.userType === roles.admin
      ? 'text-neutral-warm-500 hover:text-turquoise-300'
      : 'text-purple-100 hover:text-white';

  const activeTextColor =
    user?.userType === roles.admin ? 'text-turquoise-300' : 'text-white';

  if (!isCollapsible) {
    return (
      <motion.li
        variants={itemVariant}
        className="items-center px-6 md:border-transparent  md:border-r-4 }"
      >
        <Link href={href} as={as}>
          <div
            className={` ${
              router.pathname.includes(href)
                ? `${activeTextColor} font-semibold`
                : `${textColor} `
            }  transition-colors space-x-5 items-center text-sm md:text-[17px] py-3  flex flex-row flex-wrap w-full my-auto `}
          >
            <div className="w-4 h-4">{icon}</div>
            <span className="tracking-wide">{text}</span>
          </div>
        </Link>
      </motion.li>
    );
  }
  return (
    <motion.li
      variants={itemVariant}
      className="w-full items-center px-6 md:border-transparent  md:border-r-4 "
    >
      <button
        type="button"
        className={`${
          router.pathname.includes(href)
            ? `${activeTextColor} font-semibold`
            : `${textColor} `
        }  transition-colors space-x-5 items-center text-sm md:text-[17px] py-3  flex flex-row flex-wrap w-full my-auto `}
        onClick={toggle}
      >
        <div className="w-4 h-4">{icon}</div>

        <span className="my-auto">{text}</span>
        {isOpen ? (
          <CollapsibleArrow className="ml-auto w-5 h-5 text-gray-100  rotate-180" />
        ) : (
          <CollapsibleArrow className="ml-auto w-5 h-5 text-gray-100 rotate-90" />
        )}
      </button>
      <ul
        className={`${
          isOpen ? 'flex' : 'hidden'
        } lg:flex-col lg:min-w-full flex flex-col list-none`}
      >
        {subLinks.map((link) => (
          <li className="items-center ml-5" key={link.as}>
            <Link href={link.href} as={link.as}>
              <div
                className={` ${
                  router.pathname === link.href
                    ? `${activeTextColor} font-semibold`
                    : `${textColor} `
                }  transition-colors space-x-5 items-center text-sm md:text-[17px] py-3  flex flex-row flex-wrap w-full my-auto `}
              >
                <div className="w-4 h-4 mr-5">{link.icon}</div>
                {link.text}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </motion.li>
  );
}
